import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { projectsApi, ProjectFilters, UpdateBillingStatusPayload, BillingHistoryFilters } from '@/api/projects';

/**
 * Query key factory for projects
 */
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: ProjectFilters) => [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  billing: (id: string) => [...projectKeys.detail(id), 'billing'] as const,
  billingHistory: (id: string, filters?: BillingHistoryFilters) =>
    [...projectKeys.billing(id), 'history', filters] as const,
};

/**
 * Hook to fetch paginated list of projects
 */
export function useProjects(filters: ProjectFilters) {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: () => projectsApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

// ==================== Billing Status ====================

/**
 * Hook to fetch project with billing details
 */
export function useProjectWithBilling(id: string) {
  return useQuery({
    queryKey: projectKeys.billing(id),
    queryFn: () => projectsApi.getWithBilling(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch project billing history
 */
export function useProjectBillingHistory(id: string, filters?: BillingHistoryFilters) {
  return useQuery({
    queryKey: projectKeys.billingHistory(id, filters),
    queryFn: () => projectsApi.getBillingHistory(id, filters),
    enabled: !!id,
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to restore a soft-deleted project
 */
export function useRestoreProject() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      workspaceId,
    }: {
      id: string;
      organizationId: string;
      workspaceId: string;
    }) => projectsApi.restore(id, organizationId, workspaceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.billing(variables.id) });
      toast({
        title: 'Success',
        description: 'Project restored successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to restore project',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update project billing status
 */
export function useUpdateBillingStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateBillingStatusPayload;
    }) => projectsApi.updateBillingStatus(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.billing(variables.id) });
      toast({
        title: 'Success',
        description: `Billing status updated to ${variables.payload.billingStatus}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update billing status',
        variant: 'destructive',
      });
    },
  });
}
